import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { BarChart2, ChevronRight, Download, HelpCircle, MessageSquare, PlugZap, RotateCcw, Send, SlidersHorizontal, Trash2 } from 'lucide-react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { api, FeedbackPayload, IntegrationItem, MeData, ReferralData, SubscriptionTier } from '../api/client'
import { ErrorCard } from '../components/ui/ErrorCard'
import { RowSkeleton } from '../components/ui/Skeleton'
import { useTelegram } from '../hooks/useTelegram'
import WebApp from '@twa-dev/sdk'

const TIER_NAMES: Record<SubscriptionTier, string> = {
  free:       'Бесплатный',
  plus:       'Plus',
  pro:        'Pro',
}

// ── LimitBar ─────────────────────────────────────────────────────────────────

function LimitBar({
  label,
  used,
  limit,
  extra = 0,
}: {
  label: string
  used: number
  limit: number | null
  extra?: number
}) {
  const total = limit !== null ? limit + extra : null
  const pct =
    total !== null && total > 0
      ? Math.min(100, Math.round((used / total) * 100))
      : 0

  const valueStr =
    limit === null
      ? `${used} / ∞`
      : extra > 0
        ? `${used} / ${limit} (+${extra})`
        : `${used} / ${limit}`

  return (
    <div>
      <div className="flex justify-between items-baseline mb-1">
        <span className="text-xs text-ink-2">{label}</span>
        <span className="text-xs font-medium text-ink-1">{valueStr}</span>
      </div>
      {limit !== null && (
        <div className="h-1.5 rounded-full bg-surface-2 overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${pct}%`, background: 'var(--color-accent-gradient)' }}
          />
        </div>
      )}
    </div>
  )
}

// ── Profile page ──────────────────────────────────────────────────────────────

export function Profile() {
  const qc = useQueryClient()
  const { haptic, confirm } = useTelegram()

  const { data, isLoading, error, refetch } = useQuery<MeData>({
    queryKey: ['me'],
    queryFn: () => api.get('/api/me'),
    staleTime: 60_000,
  })

  const { data: integrations } = useQuery<IntegrationItem[]>({
    queryKey: ['integrations'],
    queryFn: () => api.get('/api/integrations'),
    staleTime: 60_000,
  })

  const { data: referralData } = useQuery<ReferralData>({
    queryKey: ['referral'],
    queryFn: () => api.get('/api/referral'),
    staleTime: 60_000,
  })

  const clearMemoryMutation = useMutation({
    mutationFn: () => api.delete('/api/memory'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['memory'] }),
  })

  const clearHistoryMutation = useMutation({
    mutationFn: () => api.delete('/api/history'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['history'] }),
  })

  const [feedbackText, setFeedbackText] = useState('')
  const [feedbackSent, setFeedbackSent] = useState(false)
  const [showDeleteSheet, setShowDeleteSheet] = useState(false)
  const [deleteSuccess, setDeleteSuccess] = useState(false)

  const feedbackMutation = useMutation({
    mutationFn: (payload: FeedbackPayload) => api.post('/api/feedback', payload),
    onSuccess: () => {
      setFeedbackSent(true)
      setFeedbackText('')
    },
  })

  const deleteAccountMutation = useMutation({
    mutationFn: () => api.delete('/api/account/delete'),
    onSuccess: () => {
      setShowDeleteSheet(false)
      setDeleteSuccess(true)
      setTimeout(() => WebApp.close(), 2000)
    },
  })

  const handleFeedbackSubmit = () => {
    if (!feedbackText.trim()) return
    haptic.impactOccurred('light')
    feedbackMutation.mutate({ text: feedbackText.trim() })
  }

  const handleClearMemory = () => {
    haptic.impactOccurred('light')
    confirm('Удалить всю память Delo о тебе? Это нельзя отменить.', ok => {
      if (!ok) return
      confirm('Ты уверен? Все факты будут удалены навсегда.', ok2 => {
        if (ok2) clearMemoryMutation.mutate()
      })
    })
  }

  const handleClearHistory = () => {
    haptic.impactOccurred('light')
    confirm('Очистить историю переписки? Это нельзя отменить.', ok => {
      if (!ok) return
      confirm('Ты уверен? Вся история сообщений будет удалена.', ok2 => {
        if (ok2) clearHistoryMutation.mutate()
      })
    })
  }

  const tier: SubscriptionTier = data?.subscription.tier ?? 'free'
  const isProPlus = tier === 'pro'
  const isPaid    = tier !== 'free'
  const initials  = data?.first_name?.[0]?.toUpperCase() ?? 'D'

  return (
    <div className="flex flex-col min-h-full page-enter">
      <div className="px-4 pt-5 pb-3">
        <p className="text-h1 text-ink">Профиль</p>
      </div>

      <div className="flex-1 px-4 pb-6 space-y-5">
        {isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => <RowSkeleton key={i} />)}
          </div>
        )}

        {error && <ErrorCard onRetry={refetch} />}

        {data && (
          <>
            {/* ── Avatar + name ── */}
            <div className="widget-enter widget-enter-1 flex flex-col items-center pt-4 pb-2 space-y-3">
              {/* Animated gradient ring */}
              <div className="avatar-ring" style={{ width: 78, height: 78 }}>
                <div
                  className="w-[72px] h-[72px] rounded-full flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, var(--color-accent), var(--color-accent-warm))',
                  }}
                >
                  <span className="font-display text-[28px] text-white leading-none">{initials}</span>
                </div>
              </div>
              <div className="text-center">
                <p className="text-h3 text-ink">{data.first_name}</p>
                {data.username && (
                  <p className="text-sm text-ink-2 mt-0.5">@{data.username}</p>
                )}
              </div>
            </div>

            {/* ── Subscription card ── */}
            <section className="widget-enter widget-enter-2">
              <p className="section-label">Подписка</p>
              <div className="bg-surface-1 border border-line-1 rounded-xl p-4 space-y-3">
                <div>
                  <p className="text-base font-medium text-ink">Delo {TIER_NAMES[tier]}</p>
                  {isPaid && (
                    <p className="text-xs text-ink-2 mt-0.5">
                      {data.subscription.expires_at
                        ? `До ${new Date(data.subscription.expires_at).toLocaleDateString('ru', { day: 'numeric', month: 'long' })}`
                        : 'Бессрочная'}
                    </p>
                  )}
                </div>

                <Link
                  to="/subscription"
                  className="flex items-center justify-between text-sm text-accent pt-0.5"
                >
                  <span>{isProPlus ? 'Изменить тариф' : 'Подробнее о тарифах'}</span>
                  <ChevronRight size={14} />
                </Link>
              </div>
            </section>

            {/* ── Usage ── */}
            <section className="widget-enter widget-enter-3">
              <p className="section-label">Использование сегодня</p>
              <div className="bg-surface-1 border border-line-1 rounded-xl p-4 space-y-2.5">
                <LimitBar
                  label="Сообщений"
                  used={data.usage.text_messages_used}
                  limit={data.usage.text_messages_limit}
                />
                <LimitBar
                  label="Голосовых"
                  used={data.usage.voice_messages_used}
                  limit={data.usage.voice_messages_limit}
                />
                <LimitBar
                  label="Фактов в памяти"
                  used={data.usage.facts_used}
                  limit={data.usage.facts_limit}
                />
                <LimitBar
                  label="Напоминаний"
                  used={data.usage.reminders_used}
                  limit={data.usage.reminders_limit}
                />
                <LimitBar
                  label="Задач"
                  used={data.usage.tasks_used}
                  limit={data.usage.tasks_limit}
                />
              </div>
            </section>

            {/* ── Referral ── */}
            <section className="widget-enter widget-enter-4">
              <p className="section-label">Реферальная программа</p>
              <div className="bg-surface-1 border border-line-1 rounded-xl p-4 space-y-3">
                <p className="text-sm text-ink-2">
                  Пригласи друга — получай процент с его платежей на баланс:
                  30% первый месяц, дальше 20%. Друг получит 3 дня Pro при знакомстве.
                  Баланс можно потратить на подписку или вывести.
                </p>
                {referralData && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-ink-2">Приглашено</span>
                      <span className="text-sm font-medium text-ink">{referralData.referral_count} чел.</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-ink-2">Баланс: <span className="font-medium text-ink">{referralData.balance_rub.toLocaleString('ru')} ₽</span></p>
                        <p className="text-xs text-ink-2 mt-0.5">Всего заработано: {referralData.total_earned_rub.toLocaleString('ru')} ₽</p>
                      </div>
                      {referralData.balance_rub >= 300 ? (
                        <button
                          onClick={() => WebApp.openTelegramLink('https://t.me/DeloAppBot?start=withdraw')}
                          className="btn-active bg-surface-2 border border-line-1 text-ink text-sm font-medium px-3 py-1.5 rounded-lg"
                        >
                          Вывести
                        </button>
                      ) : (
                        <span className="text-xs text-ink-2">Минимум 300 ₽</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0 bg-surface-2 rounded-lg px-3 py-2">
                        <p className="text-xs text-ink-1 font-mono truncate">{referralData.link}</p>
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard?.writeText(referralData.link).catch(() => {})
                          haptic.impactOccurred('light')
                        }}
                        className="btn-active shrink-0 bg-accent-dim text-accent text-xs font-medium px-3 py-2 rounded-lg"
                      >
                        Копировать
                      </button>
                    </div>
                    <button
                      onClick={() => {
                        WebApp.openTelegramLink(
                          `https://t.me/share/url?url=${encodeURIComponent(referralData.link)}&text=${encodeURIComponent('Попробуй Delo — личный ассистент в Telegram. Помогаю с делами и вопросами, запоминаю важное, напоминаю вовремя — голосом или текстом.')}`
                        )
                      }}
                      className="btn-active w-full bg-accent text-white text-sm font-medium py-2.5 rounded-lg"
                    >
                      Поделиться
                    </button>
                  </>
                )}
              </div>
            </section>

            {/* ── Настройки ──
                Раньше отсюда торчали два обрывка — голосовые ответы и ссылка на
                уведомления, — а поведение в чатах Telegram пряталось на экране
                интеграций, где его никто не искал. Теперь одна дверь. */}
            <section className="widget-enter widget-enter-5">
              <p className="section-label">Настройки</p>
              <div className="bg-surface-1 border border-line-1 rounded-xl">
                <Link
                  to="/settings"
                  className="btn-active w-full p-4 flex items-center gap-3 text-left"
                >
                  <SlidersHorizontal size={18} className="text-ink-2 shrink-0" />
                  <div className="flex-1">
                    <p className="text-base text-ink">Все настройки</p>
                    <p className="text-xs text-ink-2 mt-0.5">
                      Голос, уведомления, чаты Telegram, подключения
                    </p>
                  </div>
                  <ChevronRight size={16} className="text-ink-2" />
                </Link>
              </div>
            </section>

            {/* ── Activity ── */}
            <section>
              <p className="section-label">Активность</p>
              <div className="bg-surface-1 border border-line-1 rounded-xl">
                <Link
                  to="/stats"
                  className="btn-active w-full p-4 flex items-center gap-3 text-left"
                >
                  <BarChart2 size={18} className="text-ink-2 shrink-0" />
                  <p className="flex-1 text-base text-ink">Статистика</p>
                  <ChevronRight size={16} className="text-ink-2" />
                </Link>
              </div>
            </section>

            {/* ── Integrations ── */}
            <section>
              <p className="section-label">Интеграции</p>
              {!isPaid && data.has_paused_integrations && (
                <Link
                  to="/subscription"
                  className="block mb-2 bg-surface-2 border border-line-1 rounded-lg px-3 py-2.5"
                >
                  <p className="text-sm text-ink-2">
                    Интеграции приостановлены — подписка истекла. Продли для возобновления.
                  </p>
                </Link>
              )}
              <div className="bg-surface-1 border border-line-1 rounded-xl divide-y divide-line-1">
                <Link
                  to="/integrations"
                  className="btn-active w-full p-4 flex items-center gap-3 text-left"
                >
                  <PlugZap size={18} className="text-ink-2 shrink-0" />
                  <div className="flex-1">
                    <p className="text-base text-ink">Подключённые сервисы</p>
                    {integrations && integrations.length > 0 ? (
                      <p className="text-sm text-ink-2">
                        {integrations.length}{' '}
                        {integrations.length === 1 ? 'сервис' : integrations.length < 5 ? 'сервиса' : 'сервисов'} подключено
                      </p>
                    ) : (
                      <p className="text-sm text-ink-2">Ничего не подключено</p>
                    )}
                  </div>
                  <ChevronRight size={16} className="text-ink-2" />
                </Link>
              </div>
            </section>

            {/* ── Data ── */}
            <section>
              <p className="section-label">Данные</p>
              <div className="bg-surface-1 border border-line-1 rounded-xl divide-y divide-line-1">
                <Link
                  to="/export"
                  className="btn-active w-full p-4 flex items-center gap-3 text-left"
                >
                  <Download size={18} className="text-ink-2 shrink-0" />
                  <p className="flex-1 text-base text-ink">Мои данные</p>
                  <ChevronRight size={16} className="text-ink-2" />
                </Link>
                <button
                  onClick={handleClearMemory}
                  disabled={clearMemoryMutation.isPending}
                  className="btn-active w-full p-4 flex items-center gap-3 text-left disabled:opacity-40"
                >
                  <RotateCcw size={18} className="text-ink-2 shrink-0" />
                  <p className="flex-1 text-base text-ink">Очистить память</p>
                </button>
                <button
                  onClick={handleClearHistory}
                  disabled={clearHistoryMutation.isPending}
                  className="btn-active w-full p-4 flex items-center gap-3 text-left disabled:opacity-40"
                >
                  <Trash2 size={18} className="text-danger shrink-0" />
                  <p className="flex-1 text-base text-danger">Удалить историю</p>
                </button>
              </div>
            </section>

            {/* ── About ── */}
            <section>
              <p className="section-label">О Delo</p>
              <div className="bg-surface-1 border border-line-1 rounded-xl divide-y divide-line-1">
                <Link
                  to="/help"
                  className="btn-active w-full p-4 flex items-center gap-3 text-left"
                >
                  <HelpCircle size={18} className="text-ink-2 shrink-0" />
                  <p className="flex-1 text-base text-ink">Помощь и FAQ</p>
                  <ChevronRight size={16} className="text-ink-2" />
                </Link>
                <button
                  onClick={() => WebApp.openTelegramLink('https://t.me/zakharovsemyon')}
                  className="btn-active w-full p-4 flex items-center gap-3 text-left"
                >
                  <MessageSquare size={18} className="text-ink-2 shrink-0" />
                  <p className="flex-1 text-base text-ink">Написать в поддержку</p>
                  <ChevronRight size={16} className="text-ink-2" />
                </button>
                <div className="p-4 flex items-center">
                  <p className="flex-1 text-base text-ink-1">Версия</p>
                  <p className="text-sm text-ink-2">2.4.1</p>
                </div>
              </div>

              <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 mt-3">
                <Link to="/privacy" className="text-xs text-ink-2 underline underline-offset-2">
                  Конфиденциальность
                </Link>
                <Link to="/terms" className="text-xs text-ink-2 underline underline-offset-2">
                  Условия использования
                </Link>
                <Link to="/offer" className="text-xs text-ink-2 underline underline-offset-2">
                  Публичная оферта
                </Link>
              </div>
            </section>

            {/* ── Feedback ── */}
            <section>
              <p className="section-label">Обратная связь</p>
              <div className="bg-surface-1 border border-line-1 rounded-xl p-4 space-y-3">
                {feedbackSent ? (
                  <p className="text-sm text-ink-2 text-center py-2">Спасибо, прочитаю.</p>
                ) : (
                  <>
                    <textarea
                      value={feedbackText}
                      onChange={e => setFeedbackText(e.target.value)}
                      placeholder="Что работает хорошо? Что можно улучшить?"
                      rows={3}
                      className="w-full text-base text-ink bg-surface-2 border border-line-1 rounded-lg px-3 py-2.5 resize-none focus:outline-none focus:border-accent placeholder:text-ink-2"
                    />
                    <button
                      onClick={handleFeedbackSubmit}
                      disabled={!feedbackText.trim() || feedbackMutation.isPending}
                      className="btn-active w-full flex items-center justify-center gap-2 bg-accent text-white text-sm font-medium py-2.5 rounded-lg disabled:opacity-40"
                    >
                      <Send size={14} />
                      {feedbackMutation.isPending ? 'Отправляю…' : 'Отправить'}
                    </button>
                  </>
                )}
              </div>
            </section>

            {/* ── Delete account ── */}
            <section>
              <div className="bg-surface-1 border border-line-1 rounded-xl">
                {deleteSuccess ? (
                  <div className="p-4">
                    <p className="text-sm text-ink-2 text-center py-1">Аккаунт удалён</p>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      haptic.impactOccurred('medium')
                      setShowDeleteSheet(true)
                    }}
                    className="btn-active w-full p-4 text-center text-red-400 text-sm font-medium border border-red-400 rounded-xl"
                  >
                    Удалить аккаунт
                  </button>
                )}
              </div>
            </section>
          </>
        )}
      </div>

      {/* ── Delete bottom sheet ──
          Через портал в body: внутри страницы лист уходил под таб-бар, а тот
          не только рисуется поверх, но и перехватывает нажатия — кнопки
          «Отмена» и «Удалить» прижаты к низу ровно в перекрытую полосу.
          Тап по «Отмене» мог увести на вкладку профиля вместо закрытия. */}
      {showDeleteSheet && createPortal(
        <div
          className="fixed inset-0 flex flex-col justify-end"
          style={{ zIndex: 60, background: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowDeleteSheet(false)}
        >
          <div
            className="bg-surface-1 rounded-t-2xl p-6 space-y-4 pb-10"
            onClick={e => e.stopPropagation()}
          >
            <p className="text-h3 text-ink font-semibold">Удалить аккаунт?</p>
            <p className="text-sm text-ink-2">
              Это необратимо. Вся память, напоминания, задачи и интеграции будут удалены.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteSheet(false)}
                disabled={deleteAccountMutation.isPending}
                className="btn-active flex-1 py-3 rounded-xl bg-surface-2 border border-line-1 text-ink-1 text-sm font-medium disabled:opacity-40"
              >
                Отмена
              </button>
              <button
                onClick={() => {
                  haptic.impactOccurred('heavy')
                  deleteAccountMutation.mutate()
                }}
                disabled={deleteAccountMutation.isPending}
                className="btn-active flex-1 py-3 rounded-xl border border-red-400 text-red-400 text-sm font-medium disabled:opacity-40"
              >
                {deleteAccountMutation.isPending ? 'Удаляю…' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
